import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { isAdmin, isAgent } from '../constants/roles';
import { statusKey, statusChip } from '../constants/caseStatus';
import { priorityKey, priorityChip } from '../constants/priority';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/i18nContext';
import { useTheme } from '../context/ThemeContext';
import EmailVerificationBanner from '../components/EmailVerificationBanner';
import NewCaseModal from '../components/NewCaseModal';
import KanbanBoard from '../components/KanbanBoard';
import JobBadge from '../components/JobBadge';
import ProfileDrawer from '../components/ProfileDrawer';
import AppHeader from '../components/AppHeader';
import Button from '../components/ui/Button';
import '../styles/Dashboard.css';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');

const Dashboard = () => {
  const { user, logout, api } = useAuth();
  const { t } = useI18n();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();

  const isDark = theme === 'dark';
  const staff = isAgent(user?.role);
  const bgImage = isDark
    ? "url('/la_marina_alta_midnight_blue.webp')"
    : "url('/la_marina_alta_ocean.webp')";

  const [playAnim] = useState(() => !sessionStorage.getItem('dashboardSeen'));
  useEffect(() => {
    if (playAnim) sessionStorage.setItem('dashboardSeen', '1');
  }, [playAnim]); // eslint-disable-line react-hooks/exhaustive-deps

  const toolbarRef = useRef(null);
  const [headerHidden, setHeaderHidden] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const [cases, setCases]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope]     = useState(() => sessionStorage.getItem('dashboardScope') || '');
  const [statusFilter, setStatusFilter] = useState(() => sessionStorage.getItem('dashboardStatus') ?? 'open');
  const [showNew, setShowNew] = useState(false);
  const [profileDrawerOpen, setProfileDrawerOpen] = useState(false);
  const [stats, setStats]     = useState(null);
  const [kanbanAllowed, setKanbanAllowed] = useState(staff); // staff mindig látja
  const [siteName, setSiteName] = useState('VILLAPRESENCE');
  const [overdueOnly, setOverdueOnly] = useState(() => sessionStorage.getItem('dashboardOverdue') === '1');
  const [viewMode, setViewMode] = useState(
    () => sessionStorage.getItem('dashboardView') || 'table'
  );
  // Frissítési ütem: normál 20s; teszt módban (test_fast_poll) 1s
  const [pollMs, setPollMs] = useState(20000);

  const switchView = (mode) => {
    sessionStorage.setItem('dashboardView', mode);
    setViewMode(mode);
  };

  const applyScope = (s) => { sessionStorage.setItem('dashboardScope', s); setScope(s); };
  const applyStatus = (s) => { sessionStorage.setItem('dashboardStatus', s); setStatusFilter(s); };
  const applyOverdue = (v) => { sessionStorage.setItem('dashboardOverdue', v ? '1' : '0'); setOverdueOnly(v); };

  useEffect(() => {
    api.get('/auth/features').then(r => {
      if (!staff) setKanbanAllowed(r.data.kanban_for_customers === true);
      const name = r.data.site_name?.toUpperCase() || 'VILLAPRESENCE';
      setSiteName(name);
      document.title = r.data.site_name || 'VillaPresence';
      setPollMs(r.data.test_fast_poll === true ? 1000 : 20000);
    }).catch(() => {});
  }, [api, staff]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCases = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (staff && scope) params.set('scope', scope);
      if (statusFilter) params.set('status_group', statusFilter);
      if (overdueOnly) params.set('overdue', '1');
      const { data } = await api.get(`/cases?${params}`);
      setCases(data);
    } catch { setCases([]); }
    finally { setLoading(false); }
  }, [api, staff, scope, statusFilter, overdueOnly]);

  // Statisztika: szűrőtől független snapshot
  const loadStats = useCallback(async () => {
    try {
      const now = new Date();
      const todayStr = now.toISOString().slice(0, 10);
      const openResp = await api.get('/cases?status_group=open');
      const openCases = openResp.data;

      if (staff) {
        const [poolResp, closedResp] = await Promise.all([
          api.get('/cases?scope=pool'),
          api.get('/cases?status_group=closed'),
        ]);
        const closedToday = closedResp.data.filter(c => {
          const d = c.closed_at || c.updated_at;
          return d && d.slice(0, 10) === todayStr;
        }).length;
        setStats({
          pool:            poolResp.data.length,
          waitingForAgent: openCases.filter(c => c.status === 'waiting_for_agent').length,
          overdue:         openCases.filter(c => c.due_date && new Date(c.due_date) < now).length,
          urgent:          openCases.filter(c => c.priority === 'urgent' || c.priority === 'high').length,
          open:            openCases.length,
          closedToday,
        });
      } else {
        const closedResp = await api.get('/cases?status_group=closed');
        setStats({
          myOpen:              openCases.length,
          waitingForClient:    openCases.filter(c => c.status === 'waiting_for_client').length,
          myClosed:            closedResp.data.length,
        });
      }
    } catch { setStats(null); }
  }, [api, staff]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadCases(); }, [loadCases]);
  useEffect(() => { loadStats(); }, [loadStats]);

  // Polling — normál: 20s, teszt módban 1s; csak aktív tab esetén
  useEffect(() => {
    const tick = () => { if (!document.hidden) { loadCases(); loadStats(); } };
    const id = setInterval(tick, pollMs);
    return () => clearInterval(id);
  }, [loadCases, loadStats, pollMs]);

  // IntersectionObserver — header elrejt amikor a toolbar felgörget a képernyő tetejére
  useEffect(() => {
    const el = toolbarRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Csak akkor rejtsük el ha a toolbar a viewport FÖLÉ görgetett (nem lefelé tűnt el)
        setHeaderHidden(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      },
      { threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleLogout = async () => {
    await logout();
  };

  return (
    <div className={`dashboard-container ${isDark ? 'theme-dark' : 'theme-light'}`}>

      {/* SHARED APP HEADER */}
      <AppHeader
        siteName={siteName}
        user={user}
        onLogout={handleLogout}
        onOpenProfile={() => setProfileDrawerOpen(true)}
        headerHidden={headerHidden}
        menuOpen={menuOpen}
        onMenuToggle={setMenuOpen}
      />

      <div className="dashboard-banner-wrap">
        <EmailVerificationBanner />
      </div>

      {/* HERO */}
      <main className="dashboard-hero" style={{ backgroundImage: bgImage }}>
        <div className="hero-overlay" />
        <div className="hero-frame">
          <div className={`hero-glass${playAnim ? ' hero-glass--animate' : ''}`}>

            {/* GREETING + STATS BAR — egy sorban */}
            <div className="glass-greeting">
              <div className="greeting-text">
                <span className="greeting-label">WELCOME</span>
                <h1 className="greeting-name">{user?.username ? user.username.toUpperCase() : 'GUEST'}</h1>
              </div>

              {stats && (
                <div className="greeting-stats-bar">
                  {staff ? (
                    <>
                      <div className="gsb-item">
                        <span className="gsb-value">{stats.pool}</span>
                        <span className="gsb-label">{t('dashboard.statsPool')}</span>
                      </div>
                      <div className="gsb-item">
                        <span className="gsb-value gsb-value--urgent">{stats.waitingForAgent}</span>
                        <span className="gsb-label">{t('dashboard.statsWaiting')}</span>
                      </div>
                      <div className="gsb-item">
                        <span className={`gsb-value${stats.overdue > 0 ? ' gsb-value--danger' : ''}`}>{stats.overdue}</span>
                        <span className="gsb-label">{t('dashboard.statsOverdue')}</span>
                      </div>
                      <div className="gsb-item">
                        <span className="gsb-value">{stats.urgent}</span>
                        <span className="gsb-label">{t('dashboard.statsUrgent')}</span>
                      </div>
                      <div className="gsb-item">
                        <span className="gsb-value">{stats.open}</span>
                        <span className="gsb-label">{t('dashboard.statsOpen')}</span>
                      </div>
                      <div className="gsb-item">
                        <span className="gsb-value gsb-value--muted">{stats.closedToday}</span>
                        <span className="gsb-label">{t('dashboard.statsClosedToday')}</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="gsb-item">
                        <span className="gsb-value">{stats.myOpen}</span>
                        <span className="gsb-label">{t('dashboard.statsMyOpen')}</span>
                      </div>
                      <div className="gsb-item">
                        <span className="gsb-value gsb-value--urgent">{stats.waitingForClient}</span>
                        <span className="gsb-label">{t('dashboard.statsWaitingForClient')}</span>
                      </div>
                      <div className="gsb-item">
                        <span className="gsb-value gsb-value--muted">{stats.myClosed}</span>
                        <span className="gsb-label">{t('dashboard.statsMyClosed')}</span>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* BODY */}
            <div className="glass-body glass-body--full">

              {/* KÖZÖS TOOLBAR — mindkét nézethez */}
              <div className="kb-toolbar" ref={toolbarRef}>
                <Button variant="solid" className="kb-toolbar-new" onClick={() => setShowNew(true)}>
                  + {t('cases.newCase')}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => navigate('/contracts')}>
                  📄 {t('contracts.title')}
                </Button>
                <div className="kb-toolbar-spacer" />

                <div className="kb-toolbar-filters">
                  {staff && (
                    <>
                      <Button variant="ghost" size="sm" className={scope === 'mine' ? 'scope-active' : ''}
                        onClick={() => { applyScope('mine'); applyStatus(''); }}>
                        {t('cases.myCases')}
                      </Button>
                      {viewMode !== 'kanban' && (
                        <Button variant="ghost" size="sm" className={scope === 'pool' ? 'scope-active' : ''}
                          onClick={() => { applyScope('pool'); applyStatus(''); }}>
                          {t('cases.pool')}
                        </Button>
                      )}
                      <div className="kb-tb-sep" />
                    </>
                  )}
                  <Button variant="ghost" size="sm" className={statusFilter === 'open' ? 'scope-active' : ''}
                    onClick={() => { applyStatus('open'); applyScope(''); }}>
                    {t('cases.filterOpen')}
                  </Button>
                  {viewMode !== 'kanban' && (
                  <Button variant="ghost" size="sm" className={!statusFilter && !scope ? 'scope-active' : ''}
                    onClick={() => { applyStatus(''); applyScope(''); }}>
                    {t('cases.filterAll')}
                  </Button>
                  )}
                  {viewMode !== 'kanban' && (
                    <Button variant="ghost" size="sm" className={statusFilter === 'closed' ? 'scope-active' : ''}
                      onClick={() => { applyStatus('closed'); applyScope(''); }}>
                      {t('cases.filterClosed')}
                    </Button>
                  )}
                </div>

                {staff && (
                  <label className="kb-overdue-check">
                    <input type="checkbox" checked={overdueOnly} onChange={e => applyOverdue(e.target.checked)} />
                    <span>{t('cases.overdueOnly')}</span>
                  </label>
                )}

                {kanbanAllowed && (
                  <div className="kb-view-btns">
                    <Button variant="icon" className={viewMode === 'table' ? 'kb-view-btn--active' : ''}
                      onClick={() => switchView('table')} title="Table view">
                      ⊟
                    </Button>
                    <Button variant="icon" className={viewMode === 'kanban' ? 'kb-view-btn--active' : ''}
                      onClick={() => switchView('kanban')} title="Kanban view">
                      ⊞
                    </Button>
                  </div>
                )}
              </div>

              {/* TARTALOM */}
              {viewMode === 'kanban' ? (
                <KanbanBoard
                  cases={cases}
                  staff={staff}
                  onNavigate={navigate}
                  t={t}
                  priorityChip={priorityChip}
                  priorityKey={priorityKey}
                  statusChip={statusChip}
                  showDueDate={staff}
                />
              ) : (
                <div className="case-table-wrap">
                  <table className="case-table">
                    <thead>
                      <tr>
                        <th>{t('dashboard.caseNumber')}</th>
                        <th>{t('cases.titleLabel')}</th>
                        {staff && <th>{t('cases.customer')}</th>}
                        <th>{t('cases.agent')}</th>
                        <th>{t('priority.label')}</th>
                        <th>{t('dashboard.dateOpen')}</th>
                        {staff && <th>{t('cases.dueDate')}</th>}
                        <th>{t('dashboard.status')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cases.map(c => (
                        <tr key={c.id} className="case-row" onClick={() => navigate(`/cases/${c.id}`)}>
                          <td className="col-id">
                            {c.case_number}
                            {staff && <JobBadge count={c.active_job_count} leadStatus={c.lead_job_status} t={t} />}
                          </td>
                          <td className="col-desc">
                            {c.title}
                            {c.language && <span className="cs-lang-badge">{c.language.toUpperCase()}</span>}
                          </td>
                          {staff && <td>{c.customer_username || '—'}</td>}
                          <td className="col-agent">{c.agent_username || '—'}</td>
                          <td>
                            <span className={priorityChip(c.priority)}>{t(priorityKey(c.priority || 'normal'))}</span>
                          </td>
                          <td className="col-date">{fmtDate(c.created_at)}</td>
                          {staff && (
                            <td className={`col-date${c.due_date && new Date(c.due_date) < new Date(new Date().toDateString()) && !['resolved','closed'].includes(c.status) ? ' col-overdue' : ''}`}>
                              {c.due_date ? fmtDate(c.due_date) : '—'}
                            </td>
                          )}
                          <td className="col-status">
                            <span className={statusChip(c.status)}>{t(statusKey(c.status))}</span>
                          </td>
                        </tr>
                      ))}
                      {!loading && cases.length === 0 && (
                        <tr><td colSpan={staff ? 8 : 6} className="case-empty">{t('cases.noCases')}</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

            </div>
          </div>
        </div>
      </main>

      {showNew && (
        <NewCaseModal
          isDark={isDark}
          onClose={() => setShowNew(false)}
          onCreated={(c) => { setShowNew(false); navigate(`/cases/${c.id}`); }}
        />
      )}
      {profileDrawerOpen && user && (
        <ProfileDrawer
          userId={user.id}
          onClose={() => setProfileDrawerOpen(false)}
        />
      )}
    </div>
  );
};

export default Dashboard;
